import "./messages-queue.scss";
import { useEffect, useRef } from "react";
import { ReactComponent as CheckmarkIcon } from "../../assets/images/icons/checkmark.svg";

const SUCCESS_DURATION_MS = 3000;
const ERROR_AUTO_DISMISS_MS = 5000;

interface Message {
  id: string;
  type: "SUCCESS" | "ERROR" | string;
  message: string;
  autoDismiss?: boolean;
}

interface MessagesQueueProps {
  messages: Message[];
  removeMessage: (id: string) => void;
}

export const MessagesQueue = ({ messages, removeMessage }: MessagesQueueProps) => {
  const autoDismissTimersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  useEffect(() => {
    messages.forEach((msg) => {
      const shouldAutoDismiss = msg.type === "SUCCESS" || msg.autoDismiss === true;
      if (shouldAutoDismiss && !autoDismissTimersRef.current.has(msg.id)) {
        const duration =
          msg.type === "SUCCESS" ? SUCCESS_DURATION_MS : ERROR_AUTO_DISMISS_MS;
        const timeoutId = setTimeout(() => {
          removeMessage(msg.id);
          autoDismissTimersRef.current.delete(msg.id);
        }, duration);
        autoDismissTimersRef.current.set(msg.id, timeoutId);
      }
    });

    autoDismissTimersRef.current.forEach((timeoutId, id) => {
      const stillExists = messages.some((m) => m.id === id);
      if (!stillExists) {
        clearTimeout(timeoutId);
        autoDismissTimersRef.current.delete(id);
      }
    });
  }, [messages, removeMessage]);

  // Clear every pending timer on unmount ONLY. A re-render (e.g. a new toast
  // appearing) must not reset existing toasts' timers — each toast keeps the
  // timer it got when it first appeared and dismisses independently after its
  // own duration.
  useEffect(() => {
    const timers = autoDismissTimersRef.current;
    return () => {
      timers.forEach((timeoutId) => clearTimeout(timeoutId));
      timers.clear();
    };
  }, []);

  function toastClass(type: string) {
    if (type === "ERROR") return "mq-toast mq-toast--error";
    if (type === "SUCCESS") return "mq-toast mq-toast--success";
    return "mq-toast mq-toast--default";
  }

  return (
    <div className="mq-container" data-testid="messages-queue">
      {messages.map((message) => (
        <div key={message.id} className={toastClass(message.type)} role="alert">
          {message.type === "SUCCESS" && (
            <CheckmarkIcon className="mq-icon" aria-hidden="true" />
          )}
          <span className="mq-message">{message.message}</span>
          {message.type === "ERROR" && (
            <button
              className="mq-close"
              aria-label="close"
              onClick={() => removeMessage(message.id)}
            >
              ×
            </button>
          )}
        </div>
      ))}
    </div>
  );
};
